import React, { useState } from 'react'
import { useKeyboardShortcut } from '../../hooks/useKeyboardShortcut'
import { KeyboardShortcut } from '../../hooks/useKeyboardShortcuts'
import { useFocusTrap } from '../interlock/useFocusTrap'
import './KeyboardShortcutsHelp.css'

interface KeyboardShortcutsHelpProps {
  isOpen: boolean
  onClose: () => void
  shortcuts?: KeyboardShortcut[] // Optional: pass shortcuts from useKeyboardShortcuts hook
}

/**
 * Render visual key component (e.g., <Ctrl> + <N>)
 */
function ShortcutKey({ children }: { children: React.ReactNode }) {
  return <kbd className="shortcuts-key">{children}</kbd>
}

/**
 * Format shortcut keys for display
 */
function formatShortcutKeys(shortcut: KeyboardShortcut): React.ReactNode {
  const parts: React.ReactNode[] = []

  if (shortcut.isSequence && shortcut.sequenceKeys) {
    // Sequence shortcut (e.g., g → d)
    shortcut.sequenceKeys.forEach((key, index) => {
      parts.push(<ShortcutKey key={`key-${index}`}>{key}</ShortcutKey>)
      if (index < shortcut.sequenceKeys!.length - 1) {
        parts.push(
          <span key={`sep-${index}`} className="shortcuts-separator">
            →
          </span>
        )
      }
    })
  } else {
    // Regular shortcut with modifiers
    const isMac = navigator.platform.toLowerCase().includes('mac')

    if (shortcut.ctrl) {
      parts.push(<ShortcutKey key="ctrl">{isMac ? '⌘' : 'Ctrl'}</ShortcutKey>)
      parts.push(
        <span key="sep-ctrl" className="shortcuts-separator">
          +
        </span>
      )
    }

    if (shortcut.shift) {
      parts.push(<ShortcutKey key="shift">⇧</ShortcutKey>)
      parts.push(
        <span key="sep-shift" className="shortcuts-separator">
          +
        </span>
      )
    }

    if (shortcut.alt) {
      parts.push(<ShortcutKey key="alt">{isMac ? '⌥' : 'Alt'}</ShortcutKey>)
      parts.push(
        <span key="sep-alt" className="shortcuts-separator">
          +
        </span>
      )
    }

    if (shortcut.meta) {
      parts.push(<ShortcutKey key="meta">⌘</ShortcutKey>)
      parts.push(
        <span key="sep-meta" className="shortcuts-separator">
          +
        </span>
      )
    }

    // Main key
    const displayKey = shortcut.key === 'escape' ? 'Esc' : shortcut.key.toUpperCase()
    parts.push(<ShortcutKey key="main">{displayKey}</ShortcutKey>)
  }

  return parts
}

/**
 * Default shortcuts for backward compatibility
 */
const defaultShortcuts: KeyboardShortcut[] = [
  // Global shortcuts
  {
    key: 'k',
    ctrl: true,
    description: 'Open command palette',
    action: () => {},
    category: 'Global',
  },
  {
    key: '/',
    ctrl: true,
    description: 'Show keyboard shortcuts help',
    action: () => {},
    category: 'Global',
  },
  {
    key: 'escape',
    description: 'Close modal/dialog',
    action: () => {},
    category: 'Global',
  },
  {
    key: '?',
    shift: true,
    description: 'Show keyboard shortcuts help',
    action: () => {},
    category: 'Global',
  },

  // Navigation shortcuts
  {
    key: 'd',
    description: 'Go to Dashboard',
    action: () => {},
    category: 'Navigation',
    isSequence: true,
    sequenceKeys: ['g', 'd'],
  },
  {
    key: 'c',
    description: 'Go to Components',
    action: () => {},
    category: 'Navigation',
    isSequence: true,
    sequenceKeys: ['g', 'c'],
  },
  {
    key: 'f',
    description: 'Go to Failures (Cause & Effect)',
    action: () => {},
    category: 'Navigation',
    isSequence: true,
    sequenceKeys: ['g', 'f'],
  },
  {
    key: 'm',
    description: 'Go to Mitigations',
    action: () => {},
    category: 'Navigation',
    isSequence: true,
    sequenceKeys: ['g', 'm'],
  },
  {
    key: 'r',
    description: 'Go to Requirements',
    action: () => {},
    category: 'Navigation',
    isSequence: true,
    sequenceKeys: ['g', 'r'],
  },

  // Action shortcuts
  {
    key: 'n',
    ctrl: true,
    description: 'Create new item (context-aware)',
    action: () => {},
    category: 'Actions',
  },
  {
    key: 's',
    ctrl: true,
    description: 'Save current form',
    action: () => {},
    category: 'Actions',
  },
  {
    key: 'e',
    ctrl: true,
    description: 'Export current view',
    action: () => {},
    category: 'Actions',
  },

  // Search shortcuts
  {
    key: '/',
    description: 'Focus search input',
    action: () => {},
    category: 'Search',
  },

  // UI shortcuts
  {
    key: 'b',
    ctrl: true,
    description: 'Toggle sidebar',
    action: () => {},
    category: 'UI',
  },
]

/**
 * Keyboard Shortcuts Help Modal
 *
 * Shows all available keyboard shortcuts organized by category
 */
export function KeyboardShortcutsHelp({
  isOpen,
  onClose,
  shortcuts = defaultShortcuts,
}: KeyboardShortcutsHelpProps) {
  const [searchQuery, setSearchQuery] = useState('')

  // Close on Escape
  useKeyboardShortcut(['escape'], onClose, {
    preventDefault: true,
    ignoreInputs: false,
  })

  // A11y cheap-wins — aria-modal was present but nothing trapped Tab/Shift+Tab
  // within the panel or moved focus in on open. Shared hook (Escape here is a
  // harmless no-op duplicate of the handler above since onClose is idempotent).
  const panelRef = useFocusTrap<HTMLDivElement>(isOpen, onClose)

  // Filter shortcuts based on search
  const filteredShortcuts = searchQuery.trim()
    ? shortcuts.filter(
        (shortcut) =>
          shortcut.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
          shortcut.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
          shortcut.category.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : shortcuts

  // Get unique categories
  const categories = Array.from(new Set(filteredShortcuts.map((s) => s.category)))

  // Group by category
  const shortcutsByCategory: Record<string, KeyboardShortcut[]> = {}
  categories.forEach((category) => {
    shortcutsByCategory[category] = filteredShortcuts.filter(
      (s) => s.category === category
    )
  })

  // Close on backdrop click
  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose()
    }
  }

  if (!isOpen) {
    return null
  }

  return (
    <div
      className="shortcuts-help-backdrop"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts help"
    >
      <div ref={panelRef} className="shortcuts-help">
        <div className="shortcuts-help-header">
          <h2 className="shortcuts-help-title">Keyboard Shortcuts</h2>
          <button
            className="shortcuts-help-close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="shortcuts-help-search">
          <input
            type="text"
            className="shortcuts-help-search-input"
            placeholder="Search shortcuts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
        </div>

        <div className="shortcuts-help-content">
          {filteredShortcuts.length === 0 ? (
            <div className="shortcuts-help-empty">
              <p>No shortcuts found</p>
              <p className="shortcuts-help-empty-hint">
                Try searching for "navigation", "create", or "export"
              </p>
            </div>
          ) : (
            categories.map((category) => {
              const categoryShortcuts = shortcutsByCategory[category]
              if (!categoryShortcuts || categoryShortcuts.length === 0) {
                return null
              }

              return (
                <div key={category} className="shortcuts-help-section">
                  <h3 className="shortcuts-help-section-title">{category}</h3>
                  <div className="shortcuts-help-list">
                    {categoryShortcuts.map((shortcut, index) => (
                      <div key={index} className="shortcuts-help-item">
                        <div className="shortcuts-help-item-keys">
                          {formatShortcutKeys(shortcut)}
                        </div>
                        <div className="shortcuts-help-item-description">
                          {shortcut.description}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })
          )}
        </div>

        <div className="shortcuts-help-footer">
          <p className="shortcuts-help-footer-text">
            Press <kbd>Esc</kbd> to close • Press <kbd>Ctrl+K</kbd> to open command
            palette
          </p>
        </div>
      </div>
    </div>
  )
}
